// =============================================================================
// Báo cáo Hạ COD — gom nhóm THUẦN trên các đề nghị ĐÃ HẠ (completed) trong kỳ.
// Số lượng đề nghị nhỏ (vài chục / tháng) nên đọc dòng về rồi cộng ở trình duyệt
// như Duyệt chi; repository đọc theo lô (fetchAllPages) nên không dính trần 1000.
// =============================================================================
import type { CodReductionListRow, CodReductionStatus } from '@/types/db'
import { COD_REDUCTION_STATUSES, providerLabel, reducePercent } from './cod-reduction-utils'

export type CodReductionReportGroup = {
  label: string
  count: number
  original: number // tổng COD ban đầu
  reduce: number // tổng tiền đã hạ
}

export type CodReductionReport = {
  count: number
  original: number
  reduce: number
  percent: number // tổng hạ / tổng COD ban đầu
  bySale: CodReductionReportGroup[]
  byReason: CodReductionReportGroup[]
  byRequester: CodReductionReportGroup[]
  byProvider: CodReductionReportGroup[]
}

export const UNKNOWN_LABEL = '(không rõ)'

export function groupCodReductions(
  rows: ReadonlyArray<CodReductionListRow>,
  keyFn: (r: CodReductionListRow) => string | null | undefined,
): CodReductionReportGroup[] {
  const m = new Map<string, CodReductionReportGroup>()
  for (const r of rows) {
    const label = keyFn(r)?.trim() || UNKNOWN_LABEL
    const g = m.get(label) ?? { label, count: 0, original: 0, reduce: 0 }
    g.count += 1
    g.original += r.original_cod
    g.reduce += r.reduce_amount
    m.set(label, g)
  }
  return [...m.values()].sort(
    (a, b) => b.reduce - a.reduce || b.count - a.count || a.label.localeCompare(b.label, 'vi'),
  )
}

export function buildCodReductionReport(rows: ReadonlyArray<CodReductionListRow>): CodReductionReport {
  const original = rows.reduce((s, r) => s + r.original_cod, 0)
  const reduce = rows.reduce((s, r) => s + r.reduce_amount, 0)
  return {
    count: rows.length,
    original,
    reduce,
    percent: reducePercent(original, reduce),
    bySale: groupCodReductions(rows, (r) => r.sale?.full_name),
    byReason: groupCodReductions(rows, (r) => r.reason?.name),
    byRequester: groupCodReductions(rows, (r) => r.requester?.full_name),
    byProvider: groupCodReductions(rows, (r) => {
      const p = providerLabel(r.provider, r.provider_name)
      return p === '—' ? null : p
    }),
  }
}

export type CodReductionStatusCounts = Record<CodReductionStatus, number>

export function countByStatus(rows: ReadonlyArray<{ status: CodReductionStatus }>): CodReductionStatusCounts {
  const out = Object.fromEntries(COD_REDUCTION_STATUSES.map((s) => [s, 0])) as CodReductionStatusCounts
  for (const r of rows) if (r.status in out) out[r.status] += 1
  return out
}
